import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Calendar, Link2, BookOpen, Key, Plus, ArrowRight } from "lucide-react";

export default function CalendarDashboard() {
  const [workspaces, setWorkspaces] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/api/v1/workspaces").then(setWorkspaces).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const handleCreate = async () => {
    const name = prompt("Workspace name:");
    if (!name) return;
    try {
      const result = await api.post("/api/v1/workspaces", { name });
      setWorkspaces([{ id: result.id, name, slug: result.slug }, ...workspaces]);
      toast.success("Workspace created!");
    } catch (e: any) { toast.error(e.message); }
  };

  const quickLinks = [
    { label: "Calendar Accounts", path: "/calendar/accounts", icon: Calendar, desc: "Connect Google, Microsoft, Lark, iCloud" },
    { label: "Events", path: "/calendar/events", icon: BookOpen, desc: "View unified calendar events" },
    { label: "Share Links", path: "/calendar/share-links", icon: Link2, desc: "Create iCal subscription links" },
    { label: "Booking Pages", path: "/calendar/booking-pages", icon: BookOpen, desc: "Set up scheduling pages" },
    { label: "API Keys", path: "/calendar/api-keys", icon: Key, desc: "Manage API access" },
  ];

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">SyncBridge</h1>
          <p className="text-sm text-muted-foreground mt-1">Calendar aggregation and scheduling</p>
        </div>
        <button onClick={handleCreate} className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 flex items-center gap-2">
          <Plus className="w-4 h-4" /> New Workspace
        </button>
      </div>

      {/* Workspaces */}
      {loading ? (
        <div className="animate-pulse space-y-3 mb-8">{[1,2].map(i=><div key={i} className="h-16 bg-muted rounded-xl"/>)}</div>
      ) : workspaces.length > 0 ? (
        <div className="space-y-2 mb-8">
          <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">Your Workspaces</h2>
          {workspaces.map(w => (
            <div key={w.id} className="bg-card rounded-xl border border-border p-4 flex items-center gap-4">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Calendar className="w-5 h-5 text-primary" />
              </div>
              <div className="flex-1">
                <p className="font-medium">{w.name}</p>
                <p className="text-xs text-muted-foreground">Role: {w.member_role || "owner"}</p>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-card rounded-xl border border-dashed border-border p-8 text-center mb-8">
          <Calendar className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
          <p className="font-medium mb-1">No workspaces yet</p>
          <p className="text-sm text-muted-foreground mb-4">Create a workspace to start connecting calendars.</p>
          <button onClick={handleCreate} className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:opacity-90">
            Create Workspace
          </button>
        </div>
      )}

      {/* Quick links */}
      <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">Quick Access</h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {quickLinks.map(l => (
          <Link key={l.path} to={l.path} className="bg-card rounded-xl border border-border p-5 hover:shadow-md transition-shadow group">
            <l.icon className="w-6 h-6 text-primary mb-3" />
            <p className="font-medium text-foreground flex items-center gap-2">
              {l.label}
              <ArrowRight className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 transition-opacity" />
            </p>
            <p className="text-xs text-muted-foreground mt-1">{l.desc}</p>
          </Link>
        ))}
      </div>
    </div>
  );
}
